import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';

interface SiteSettingsData {
  site: {
    title: string;
    description: string;
    language: string;
    version: number;
    slug?: string | null;
  };
  publishedUrl?: string;
}

interface SiteSettingsSite {
  title: string;
  description: string;
  language: string;
  version: number;
  slug?: string | null;
}

interface PublishResponse {
  url: string;
  versionId: string;
  versionNo: number;
}

export function SiteSettingsPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const [site, setSite] = useState<SiteSettingsSite | null>(null);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('en');
  const [slug, setSlug] = useState('');

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    api
      .get<SiteSettingsData>(`/sites/${siteId}`)
      .then((data) => {
        if (cancelled) return;
        setSite(data.site);
        setPublishedUrl(data.publishedUrl ?? null);
        setTitle(data.site.title);
        setDescription(data.site.description);
        setLanguage(data.site.language);
        setSlug(data.site.slug ?? '');
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load site.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!site || !siteId) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const data = await api.patch<{ site: SiteSettingsSite }>(`/sites/${siteId}`, {
        version: site.version,
        title,
        description,
        language,
        slug,
      });
      setSite(data.site);
      setMessage('Site settings saved.');
    } catch (err) {
      setError((err as Error).message || 'Could not save site settings.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!siteId) return;
    setPublishBusy(true);
    setError(null);
    setMessage(null);
    try {
      const data = await api.post<PublishResponse>(`/sites/${siteId}/publish`, {});
      setPublishedUrl(data.url);
      setMessage('Site published.');
    } catch (err) {
      setError((err as Error).message || 'Could not publish site.');
    } finally {
      setPublishBusy(false);
    }
  }

  async function handleUnpublish() {
    if (!siteId) return;
    setPublishBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/sites/${siteId}/unpublish`, {});
      setPublishedUrl(null);
      setMessage('Site unpublished.');
    } catch (err) {
      setError((err as Error).message || 'Could not unpublish site.');
    } finally {
      setPublishBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="dash-page">
        <p className="dash-page-subtitle">Loading site settings…</p>
      </div>
    );
  }

  if (error && !site) {
    return (
      <div className="dash-page">
        <div className="sarnia-card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <h1>Site settings</h1>
        <Link to={`/editor/${siteId}`} className="sarnia-btn sarnia-btn--secondary">
          Open editor
        </Link>
      </div>

      <section className="sarnia-card dash-settings-card">
        <h2>Details</h2>
        <p>Manage the core information for this site.</p>
        <form onSubmit={handleSave}>
          <div className="dash-field">
            <label htmlFor="title">Site title</label>
            <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="dash-field">
            <label htmlFor="description">Description</label>
            <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </div>
          <div className="dash-field">
            <label htmlFor="language">Language</label>
            <input id="language" type="text" value={language} onChange={(e) => setLanguage(e.target.value)} maxLength={5} />
          </div>
          <div className="dash-field">
            <label htmlFor="slug">Site address</label>
            <div className="dash-slug-input">
              <input id="slug" type="text" value={slug} onChange={(e) => setSlug(e.target.value)} pattern="[a-z0-9-]{3,40}" />
              <span>.sarnia.digital</span>
            </div>
            <p className="dash-page-subtitle">Lowercase letters, numbers and hyphens. 3–40 characters.</p>
          </div>
          {message && (
            <div className="sarnia-card" style={{ color: message.startsWith('Site') ? 'var(--success)' : 'var(--danger)' }}>
              {message}
            </div>
          )}
          {error && (
            <div className="sarnia-card" style={{ color: 'var(--danger)' }}>
              {error}
            </div>
          )}
          <button type="submit" className="sarnia-btn sarnia-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      </section>

      <section className="sarnia-card dash-settings-card">
        <h2>Publishing</h2>
        {publishedUrl ? (
          <p>
            Live at{' '}
            <a href={`https://${publishedUrl}`} target="_blank" rel="noreferrer">
              {publishedUrl}
            </a>
          </p>
        ) : (
          <p>This site is not published.</p>
        )}
        <div className="dash-actions">
          <button type="button" className="sarnia-btn sarnia-btn--primary" onClick={handlePublish} disabled={publishBusy}>
            {publishBusy ? 'Working…' : 'Publish'}
          </button>
          <button type="button" className="sarnia-btn sarnia-btn--secondary" onClick={handleUnpublish} disabled={publishBusy}>
            Unpublish
          </button>
        </div>
      </section>
    </div>
  );
}
